import React, { useState, useEffect, useCallback } from 'react';
import './LogsWhatsappData.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle, faClock, faBan, faTimesCircle, faSync, faFilePdf,
    faSearch, faChevronLeft, faChevronRight, faCircleInfo
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { apiRaw } from '../../../api';

const PER_PAGE = 50;

// Metadatos de cada estado (icono FontAwesome + color) — sin emojis.
const ESTADOS = {
    enviada:   { label: 'Enviadas',   icon: faCheckCircle,  clase: 'estado-enviada' },
    pendiente: { label: 'Pendientes', icon: faClock,        clase: 'estado-pendiente' },
    expirada:  { label: 'Expiradas',  icon: faBan,          clase: 'estado-expirada' },
    error:     { label: 'Errores',    icon: faTimesCircle,  clase: 'estado-error' },
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

export default function LogsWhatsappData() {
    const [filas, setFilas] = useState([]);
    const [resumen, setResumen] = useState({ enviada: 0, pendiente: 0, expirada: 0, error: 0 });
    const [totalRecords, setTotalRecords] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [filtros, setFiltros] = useState({
        estado: '', tipo: '', destinatario: '', desde: '', hasta: '', q: '',
    });

    const setFiltro = (campo, valor) => {
        setFiltros((f) => ({ ...f, [campo]: valor }));
        setPage(1);
    };

    // Click en un chip del resumen: alterna el filtro de estado.
    const toggleEstado = (estado) => {
        setFiltros((f) => ({ ...f, estado: f.estado === estado ? '' : estado }));
        setPage(1);
    };

    const cargar = useCallback((signal) => {
        setLoading(true);
        const params = new URLSearchParams({ page, per_page: PER_PAGE });
        Object.entries(filtros).forEach(([k, v]) => { if (v) params.append(k, v); });

        apiRaw(`/notificaciones?${params.toString()}`, { method: 'GET', signal })
            .then((r) => r.json())
            .then((data) => {
                setFilas(data.data || []);
                setResumen(data.resumen || { enviada: 0, pendiente: 0, expirada: 0, error: 0 });
                setTotalRecords(data.totalRecords || 0);
                setLastPage(data.lastPage || 1);
            })
            .catch((e) => { if (e.name !== 'AbortError') console.error('Error cargando logs:', e); })
            .finally(() => setLoading(false));
    }, [filtros, page]);

    // Debounce: al cambiar filtros o página, recarga tras 300ms (aborta la anterior).
    useEffect(() => {
        const ctrl = new AbortController();
        const t = setTimeout(() => cargar(ctrl.signal), 300);
        return () => { clearTimeout(t); ctrl.abort(); };
    }, [cargar]);

    return (
        <div className='logsWhatsapp'>
            <h2 className='logs-titulo'>
                <FontAwesomeIcon icon={faWhatsapp} /> Logs de WhatsApp
            </h2>

            {/* Resumen: chips por estado (clic para filtrar) */}
            <div className='logs-resumen'>
                {Object.entries(ESTADOS).map(([key, meta]) => (
                    <button
                        key={key}
                        className={`logs-chip ${meta.clase} ${filtros.estado === key ? 'chip-activo' : ''}`}
                        onClick={() => toggleEstado(key)}
                        title={`Filtrar por ${meta.label.toLowerCase()}`}
                    >
                        <FontAwesomeIcon icon={meta.icon} />
                        <span className='chip-num'>{resumen[key]}</span>
                        <span className='chip-label'>{meta.label}</span>
                    </button>
                ))}
            </div>

            {/* Filtros */}
            <div className='filtrosContain'>
                <div className='inputsColumn'>
                    <input
                        type='text'
                        value={filtros.q}
                        onChange={(e) => setFiltro('q', e.target.value)}
                        placeholder='Buscar cliente, terapeuta o teléfono'
                    />
                </div>
                <div className='inputsColumn'>
                    <select value={filtros.tipo} onChange={(e) => setFiltro('tipo', e.target.value)}>
                        <option value=''>Todos los tipos</option>
                        <option value='recordatorio'>Recordatorio</option>
                        <option value='confirmacion'>Confirmación</option>
                        <option value='cancelacion'>Cancelación</option>
                    </select>
                </div>
                <div className='inputsColumn'>
                    <select value={filtros.destinatario} onChange={(e) => setFiltro('destinatario', e.target.value)}>
                        <option value=''>Cliente y terapeuta</option>
                        <option value='cliente'>Solo cliente</option>
                        <option value='terapeuta'>Solo terapeuta</option>
                    </select>
                </div>
                <div className='inputsColumn'>
                    <select value={filtros.estado} onChange={(e) => setFiltro('estado', e.target.value)}>
                        <option value=''>Todos los estados</option>
                        <option value='enviada'>Enviadas</option>
                        <option value='pendiente'>Pendientes</option>
                        <option value='expirada'>Expiradas</option>
                        <option value='error'>Errores</option>
                    </select>
                </div>
                <div className='inputsColumn'>
                    <label className='logs-fecha-label'>Desde</label>
                    <input type='date' value={filtros.desde} onChange={(e) => setFiltro('desde', e.target.value)} />
                </div>
                <div className='inputsColumn'>
                    <label className='logs-fecha-label'>Hasta</label>
                    <input type='date' value={filtros.hasta} onChange={(e) => setFiltro('hasta', e.target.value)} />
                </div>
                <button className='reload' onClick={() => cargar()} title='Recargar'>
                    <FontAwesomeIcon icon={faSync} />
                </button>
            </div>

            {/* Leyenda de estados */}
            <p className='logs-leyenda'>
                <FontAwesomeIcon icon={faCircleInfo} />{' '}
                <b>Enviada</b>: salió por WhatsApp · <b>Pendiente</b>: espera su fecha ·{' '}
                <b>Expirada</b>: no salió su día y ya no se reintenta · <b>Error</b>: falló el envío.
                Las fechas están en hora de México.
            </p>

            {/* Tabla */}
            <div className='table-container'>
                <table className='table'>
                    <thead>
                        <tr>
                            <th>Estado</th>
                            <th>Tipo</th>
                            <th>Para</th>
                            <th>Cliente</th>
                            <th>Terapeuta</th>
                            <th>Fecha cita</th>
                            <th>Sede</th>
                            <th>Programada</th>
                            <th>Enviada</th>
                            <th>PDF</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan='10' className='logs-vacio'><FontAwesomeIcon icon={faSync} spin /> Cargando…</td></tr>
                        ) : filas.length === 0 ? (
                            <tr><td colSpan='10' className='logs-vacio'>Sin notificaciones con estos filtros.</td></tr>
                        ) : (
                            filas.map((n) => {
                                const meta = ESTADOS[n.estado] || { icon: faCircleInfo, clase: '' };
                                return (
                                    <tr key={n.id}>
                                        <td>
                                            <span className={`logs-badge ${meta.clase}`}>
                                                <FontAwesomeIcon icon={meta.icon} /> {cap(n.estado)}
                                            </span>
                                        </td>
                                        <td>{cap(n.tipo)}</td>
                                        <td>{cap(n.destinatario)}</td>
                                        <td>{n.cliente || '—'}</td>
                                        <td>{n.terapeuta || '—'}</td>
                                        <td>{n.fecha_cita || '—'}</td>
                                        <td>{n.sede || '—'}</td>
                                        <td>{n.programada || '—'}</td>
                                        <td>{n.enviada || '—'}</td>
                                        <td>
                                            {n.pdf_url ? (
                                                <a href={n.pdf_url} target='_blank' rel='noreferrer' title='Ver PDF de la cita'>
                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                </a>
                                            ) : '—'}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            <div className='logs-paginacion'>
                <span className='logs-total'>{totalRecords} registro(s)</span>
                <div className='logs-pag-btns'>
                    <button disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span>Página {page} de {lastPage}</span>
                    <button disabled={page >= lastPage || loading} onClick={() => setPage((p) => p + 1)}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>
        </div>
    );
}
